import {
  HttpClient,
  HttpErrorResponse,
  HttpParams,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { parseISO } from 'date-fns';
import { throwError, Observable } from 'rxjs';
import { catchError, map, reduce } from 'rxjs/operators';

import {
  ChangeRequestDetails,
  FindAllApiResponse,
  Hazmat,
  RecertificationDetails,
  TruxDescription,
  FindCardApiResponse,
  WasteProfile,
  WasteProfileAdminCard,
  WasteProfileCustomerCard,
  FindCustomerCardApiResponse,
  FindWasteProfileSearchApiResponse,
  WasteProfileStatus,
} from '@digital-platform/waste-profiles/domain';

type WasteProfileResponse = Omit<
  WasteProfile,
  | 'createdDate'
  | 'lastModifiedDate'
  | 'submittedDate'
  | 'expectedResponseDate'
  | 'approvedDate'
  | 'rejectedDate'
  | 'incompleteDate'
  | 'expiryDate'
> & {
  createdDate: string;
  lastModifiedDate: string;
  submittedDate: string;
  expectedResponseDate: string;
  approvedDate: string;
  rejectedDate: string;
  incompleteDate: string;
  expiryDate: string;
};

type WasteProfileCardResponse = Omit<
  WasteProfileAdminCard,
  'lastModifiedDate' | 'submittedDate' | 'approvedDate' | 'expiryDate'
> & {
  lastModifiedDate: string;
  submittedDate: string;
  approvedDate: string;
  expiryDate: string;
};

type WasteProfileCustomerCardResponse = Omit<
  WasteProfileCustomerCard,
  'lastModifiedDate' | 'expiryDate'
> & {
  lastModifiedDate: string;
  expiryDate: string;
};

@Injectable({
  providedIn: 'root',
})
export class WasteProfilesService {
  constructor(private httpClient: HttpClient) {}

  public create(newWasteProfile: WasteProfile): Observable<WasteProfile> {
    const url = '/api/waste-profiles';
    const body = newWasteProfile;
    return this.httpClient
      .post<WasteProfileResponse>(url, body)
      .pipe(catchError(this.handleError), map(this.parseDates));
  }

  public update(
    id: string,
    wasteProfile: WasteProfile
  ): Observable<WasteProfile> {
    const url = `/api/waste-profiles/${id}`;
    const body = wasteProfile;
    return this.httpClient
      .put<WasteProfileResponse>(url, body)
      .pipe(catchError(this.handleError), map(this.parseDates));
  }

  public delete(id: string): Observable<WasteProfile> {
    const url = `/api/waste-profiles/${id}`;
    return this.httpClient
      .delete<WasteProfile>(url)
      .pipe(catchError(this.handleError));
  }

  public activate(
    id: string,
    wasteProfile: WasteProfile
  ): Observable<WasteProfile> {
    const url = `/api/waste-profiles/${id}/activate`;
    const body = wasteProfile;
    return this.httpClient
      .put<WasteProfileResponse>(url, body)
      .pipe(catchError(this.handleError), map(this.parseDates));
  }

  public checkUserSignatureAuth(id: string): Observable<boolean> {
    const url = `api/waste-profiles/${id}/sig-auth`;
    return this.httpClient.get<boolean>(url).pipe(catchError(this.handleError));
  }

  public recallSignature(id: string): Observable<WasteProfile> {
    const url = `/api/waste-profiles/${id}/recall-signature`;
    return this.httpClient
      .put<WasteProfile>(url, null)
      .pipe(catchError(this.handleError));
  }

  public chooseSigner(
    type: string,
    id: string,
    name: string,
    email: string
  ): Observable<WasteProfile> {
    const url = `api/signing/${type}/${id}/choose-signer?signerName=${name}&signerEmail=${email}`;
    return this.httpClient
      .get<WasteProfileResponse>(url)
      .pipe(catchError(this.handleError), map(this.parseDates));
  }

  public sendDesignatedSignerEmails(
    type: string,
    id: string
  ): Observable<WasteProfile> {
    const url = `api/signing/${type}/${id}/send-designated-signer-emails`;
    return this.httpClient
      .get<WasteProfileResponse>(url)
      .pipe(catchError(this.handleError), map(this.parseDates));
  }

  public sendSignerInfo(
    id: string,
    name: string,
    email: string
  ): Observable<WasteProfile> {
    const url = `api/waste-profiles/${id}/set-signer?signerName=${name}&signerEmail=${email}`;
    return this.httpClient
      .get<WasteProfileResponse>(url)
      .pipe(catchError(this.handleError), map(this.parseDates));
  }

  public recertify(
    id: string,
    recertificationDetails: RecertificationDetails
  ): Observable<WasteProfile> {
    const url = `/api/waste-profiles/${id}/recertify`;
    const body = recertificationDetails;
    return this.httpClient
      .post<WasteProfileResponse>(url, body)
      .pipe(catchError(this.handleError), map(this.parseDates));
  }

  public change(
    id: string,
    changeRequestDetails: ChangeRequestDetails
  ): Observable<WasteProfile> {
    const url = `/api/waste-profiles/${id}/change`;
    const body = changeRequestDetails;
    return this.httpClient
      .post<WasteProfileResponse>(url, body)
      .pipe(catchError(this.handleError), map(this.parseDates));
  }

  public duplicate(wasteProfileId: string): Observable<WasteProfile> {
    const url = '/api/waste-profiles/duplicate';
    const body = {
      wasteProfileId,
    };
    return this.httpClient
      .post<WasteProfile>(url, body)
      .pipe(catchError(this.handleError));
  }

  public findById(id: string): Observable<WasteProfile> {
    const url = `/api/waste-profiles/${id}`;
    const httpOptions = {};
    return this.httpClient
      .get<WasteProfileResponse>(url, httpOptions)
      .pipe(catchError(this.handleError), map(this.parseDates));
  }

  public findAll(ExclusiveStartKey?: string): Observable<FindAllApiResponse> {
    const url = '/api/waste-profiles';
    const params = { pageSize: '250' };
    const headers = ExclusiveStartKey
      ? {
          'X-RSC-Page': ExclusiveStartKey,
        }
      : {};
    return this.getResponse(url, params, headers).pipe(
      map(
        (
          response: HttpResponse<WasteProfileResponse[]>
        ): FindAllApiResponse => {
          const LastEvaluatedKey = response?.headers?.get('X-RSC-Page');
          return {
            LastEvaluatedKey,
            Items: response?.body.map(this.parseDates),
          };
        }
      ),
      catchError(this.handleError)
    );
  }

  public search(
    searchby: {
      key: string;
      value: any;
    }[],
    searchLoopId: string,
    ExclusiveStartKey?: string
  ): Observable<FindWasteProfileSearchApiResponse> {
    const url = 'api/waste-profiles/search';
    const params = { pageSize: '500' };
    const headers = ExclusiveStartKey
      ? {
          'X-RSC-Page': ExclusiveStartKey,
        }
      : {};

    headers['X-Loading-Id'] = searchLoopId;
    for (const iterator of searchby) {
      params[iterator.key] = iterator.value;
    }
    return this.getResponse(url, params, headers).pipe(
      map((response: HttpResponse<any>): FindWasteProfileSearchApiResponse => {
        const LastEvaluatedKey = response?.headers?.get('X-RSC-Page');
        return {
          LastEvaluatedKey,
          Items: response?.body,
        };
      }),
      catchError(this.handleError)
    );
  }

  public findAllStatusCards(options?: {
    status: WasteProfileStatus;
    ExclusiveStartKey?: string;
    customerName?: string;
    divisionNumber?: string;
    wpLoadingUuid: string;
  }): Observable<FindCustomerCardApiResponse> {
    const {
      customerName,
      divisionNumber,
      ExclusiveStartKey,
      status,
      wpLoadingUuid,
    } = options;
    const url = '/api/waste-profiles/cards';
    const params = customerName
      ? { pageSize: '500', customerName, status }
      : { pageSize: '500', divisionNumber, status };
    const headers = ExclusiveStartKey
      ? {
          'X-RSC-Page': ExclusiveStartKey,
        }
      : {};

    headers['X-Loading-Id'] = wpLoadingUuid;

    return this.getCardCustomerResponse(url, params, headers).pipe(
      map(
        (
          response: HttpResponse<WasteProfileCustomerCardResponse[]>
        ): FindCustomerCardApiResponse => {
          const LastEvaluatedKey = response?.headers?.get('X-RSC-Page');
          return {
            LastEvaluatedKey,
            Items: response?.body.map(this.parseCustomerCardDates),
          };
        }
      ),
      catchError(this.handleError)
    );
  }
  public findAllInternalStatusCards(options: {
    divisionNumber?: string;
    ExclusiveStartKey?: string;
    wpLoadingUuid: string;
  }): Observable<FindCardApiResponse> {
    const { divisionNumber, ExclusiveStartKey, wpLoadingUuid } = options;
    const url = '/api/waste-profiles/cards';
    const params = { pageSize: '500' };

    if (divisionNumber) {
      params['divisionNumber'] = divisionNumber;
    }

    const headers = ExclusiveStartKey
      ? {
          'X-RSC-Page': ExclusiveStartKey,
        }
      : {};

    headers['X-Loading-Id'] = wpLoadingUuid;

    return this.getCardResponse(url, params, headers).pipe(
      map(
        (
          response: HttpResponse<WasteProfileCardResponse[]>
        ): FindCardApiResponse => {
          const LastEvaluatedKey = response?.headers?.get('X-RSC-Page');
          return {
            LastEvaluatedKey,
            Items: response?.body.map(this.parseCardDates),
          };
        }
      ),
      catchError(this.handleError)
    );
  }

  public getHazmats(): Observable<Hazmat[]> {
    const url = 'api/waste-profiles/hazmats';
    return this.httpClient.get<Hazmat[]>(url).pipe(
      map((response) => response),
      catchError(this.handleError)
    );
  }

  /**
   * @deprecated - Not implemented for searching server side
   * @param searchby
   * @returns
   */
  public searchWithHazmats(
    searchby: {
      key: string;
      value: any;
    }[]
  ) {
    const url = 'api/waste-profiles/hazmats';
    const params = {};
    for (const iterator of searchby) {
      params[iterator.key] = iterator.value;
    }
    return this.getResponse(url, params).pipe(
      map((response) => response.body),
      reduce((hazmats, thisPage) => [...hazmats, ...thisPage], []),
      catchError(this.handleError)
    );
  }

  public getTruxMaterial(): Observable<TruxDescription[]> {
    const url = 'api/waste-profiles/trux-descriptions';
    return this.httpClient.get<TruxDescription[]>(url).pipe(
      map((response) => response),
      catchError(this.handleError)
    );
  }

  private getResponse(
    url: string,
    params?:
      | HttpParams
      | {
          [param: string]: string | string[];
        },
    headers?: {
      [param: string]: string | string[];
    }
  ): Observable<HttpResponse<WasteProfileResponse[]>> {
    return this.httpClient.get<WasteProfileResponse[]>(url, {
      observe: 'response',
      params,
      headers,
    });
  }

  private getCardResponse(
    url: string,
    params?:
      | HttpParams
      | {
          [param: string]: string | string[];
        },
    headers?: {
      [param: string]: string | string[];
    }
  ): Observable<HttpResponse<WasteProfileCardResponse[]>> {
    return this.httpClient.get<WasteProfileCardResponse[]>(url, {
      observe: 'response',
      params,
      headers,
    });
  }

  private getCardCustomerResponse(
    url: string,
    params?:
      | HttpParams
      | {
          [param: string]: string | string[];
        },
    headers?: {
      [param: string]: string | string[];
    }
  ): Observable<HttpResponse<WasteProfileCustomerCardResponse[]>> {
    return this.httpClient.get<WasteProfileCustomerCardResponse[]>(url, {
      observe: 'response',
      params,
      headers,
    });
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong.
      console.error(error);

      switch (error.status) {
        case 403:
          return throwError(
            'Access denied while loading Waste Profiles, please try again later or contact Republic Services'
          );
      }
    }

    let message = 'Something bad happened; please try again later.';

    if (error.error?.message) {
      message = error.error.message;
    }

    // Return an observable with a user-facing error message.
    return throwError(message);
  }

  private parseDates(wasteProfile: WasteProfileResponse): WasteProfile {
    const dateOrNull = (date: string): Date | null => {
      const parsed = parseISO(date);

      return parsed.toString() !== 'Invalid Date' ? parsed : null;
    };

    return {
      ...wasteProfile,
      createdDate: dateOrNull(wasteProfile.createdDate),
      lastModifiedDate: dateOrNull(wasteProfile.lastModifiedDate),
      submittedDate: dateOrNull(wasteProfile.submittedDate),
      expectedResponseDate: dateOrNull(wasteProfile.expectedResponseDate),
      approvedDate: dateOrNull(wasteProfile.approvedDate),
      rejectedDate: dateOrNull(wasteProfile.rejectedDate),
      incompleteDate: dateOrNull(wasteProfile.incompleteDate),
      expiryDate: dateOrNull(wasteProfile.expiryDate),
    };
  }

  private parseCardDates(
    wasteProfileCard: WasteProfileCardResponse
  ): WasteProfileAdminCard {
    const dateOrNull = (date: string): Date | null => {
      const parsed = parseISO(date);

      return parsed.toString() !== 'Invalid Date' ? parsed : null;
    };

    return {
      ...wasteProfileCard,
      lastModifiedDate: dateOrNull(wasteProfileCard.lastModifiedDate),
      submittedDate: dateOrNull(wasteProfileCard.submittedDate),
      approvedDate: dateOrNull(wasteProfileCard.approvedDate),
      expiryDate: dateOrNull(wasteProfileCard.expiryDate),
    };
  }

  private parseCustomerCardDates(
    wasteProfileCard: WasteProfileCustomerCardResponse
  ): WasteProfileCustomerCard {
    const dateOrNull = (date: string): Date | null => {
      const parsed = parseISO(date);

      return parsed.toString() !== 'Invalid Date' ? parsed : null;
    };

    return {
      ...wasteProfileCard,
      lastModifiedDate: dateOrNull(wasteProfileCard.lastModifiedDate),
      expiryDate: dateOrNull(wasteProfileCard.expiryDate),
    };
  }
}
